// CrewScoreDashboard.js
import React, { useState } from "react";
import {
  Container,
  Typography,
  Grid,
  Slider,
  Paper,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Box
} from "@material-ui/core";
import { calculateCrewEfficiency, DEFAULT_WEIGHTS } from "./crewEfficiencyCalculator";

const defaultCrewTemplate = (id) => ({
  crewId: `crew_${id}`,
  shiftsAssigned: 20,
  daysAbsent: 2,
  daysLate: 5,
  avgMinutesLate: 6,
  breaksMissed: 3,
  lunchesMissed: 1,
  idleMinutes: 45,
  jobsAssigned: 20,
  jobsCompleted: 17,
  reworks: 1,
  avgReviewScore: 4.2,
  photoCompliancePct: 85,
  totalMilesDriven: 130,
  expectedMiles: 120,
  valueGenerated: 3800,
  crewCost: 950,
  targetDollarPerHourRatio: 4.0
});

const CrewScoreDashboard = () => {
  const [weights, setWeights] = useState(DEFAULT_WEIGHTS);
  const [crewInputs, setCrewInputs] = useState([
    defaultCrewTemplate("alpha")
  ]);

  const handleWeightChange = (category) => (_, newValue) => {
    const newWeights = { ...weights, [category]: newValue };
    const total = Object.values(newWeights).reduce((a, b) => a + b, 0);
    if (total <= 100) setWeights(newWeights);
  };

  const handleCrewChange = (index, field) => (e) => {
    const value = e.target.type === "number" ? parseFloat(e.target.value) || 0 : e.target.value;
    setCrewInputs((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        [field]: value
      };
      return updated;
    });
  };

  const addCrew = () => {
    if (crewInputs.length < 3) {
      const newId = `crew_${String.fromCharCode(98 + crewInputs.length)}`; // crew_b, crew_c
      setCrewInputs((prev) => [...prev, defaultCrewTemplate(newId)]);
    }
  };

  const scoredCrews = crewInputs.map((crew) =>
    calculateCrewEfficiency(crew, weights)
  );

  const crewFields = [
    ["shiftsAssigned", "Shifts Assigned"],
    ["daysAbsent", "Days Absent"],
    ["daysLate", "Days Late"],
    ["avgMinutesLate", "Avg Minutes Late"],
    ["breaksMissed", "Breaks Missed"],
    ["lunchesMissed", "Lunches Missed"],
    ["idleMinutes", "Idle Minutes"],
    ["jobsAssigned", "Jobs Assigned"],
    ["jobsCompleted", "Jobs Completed"],
    ["reworks", "Reworks"],
    ["avgReviewScore", "Avg Review Score"],
    ["photoCompliancePct", "Photo Compliance (%)"],
    ["totalMilesDriven", "Miles Driven"],
    ["expectedMiles", "Expected Miles"],
    ["valueGenerated", "Value Generated ($)"],
    ["crewCost", "Crew Cost ($)"],
    ["targetDollarPerHourRatio", "Target $/hr"]
  ];

  const totalWeight = Object.values(weights).reduce((a, b) => a + b, 0);

  return (
    <Container>
      <Typography variant="h4" gutterBottom>
        Crew Efficiency Scoring Tool
      </Typography>

      <Paper style={{ padding: 16, marginBottom: 24 }}>
        <Typography variant="h6">Adjust KPI Weights</Typography>
        <Grid container spacing={2}>
          {Object.entries(weights).map(([key, value]) => (
            <Grid item xs={6} sm={4} md={3} key={key}>
              <Typography>{key} ({value})</Typography>
              <Slider
                value={value}
                onChange={handleWeightChange(key)}
                min={0}
                max={100}
                step={1}
                valueLabelDisplay="auto"
                disabled={totalWeight >= 100 && value === 0}
              />
            </Grid>
          ))}
        </Grid>
        <Box mt={2}><Typography>Total Weight: {totalWeight} (Max: 100)</Typography></Box>
      </Paper>

      {scoredCrews.map((crew, index) => (
        <Paper key={crew.crewId} style={{ padding: 16, marginBottom: 24 }}>
          <Typography variant="h6">{crew.crewId} Report Card</Typography>
          <Grid container spacing={2}>
            {crewFields.map(([field, label]) => (
              <Grid item xs={6} sm={4} md={3} key={field}>
                <TextField
                  label={label}
                  type="number"
                  value={crewInputs[index][field]}
                  onChange={handleCrewChange(index, field)}
                  fullWidth
                />
              </Grid>
            ))}
          </Grid>
          <Box mt={2}>
            <Typography><strong>Total Score:</strong> {crew.summaryScore}</Typography>
            <Typography><strong>Job Completion:</strong> {crew.components?.jobCompletion?.score}</Typography>
            <Typography><strong>Quality:</strong> {crew.components?.quality?.score}</Typography>
            <Typography><strong>Revenue $/hr:</strong> {crew.components?.revenueEfficiency?.dollarPerHour?.toFixed(2)}</Typography>
          </Box>
        </Paper>
      ))}

      <Box textAlign="center" my={4}>
        <Button variant="contained" color="primary" onClick={addCrew} disabled={crewInputs.length >= 3}>
          Add Another Crew
        </Button>
      </Box>
    </Container>
  );
};

export default CrewScoreDashboard;
